import { useCallback, useEffect, useMemo, useState } from "react";

import {
  crearMeta,
  editarMeta,
  listarTerritorio,
  verTablero,
  type Meta,
  type NodoTerritorio,
  type Yo,
} from "./api";
import { asignables as soloAsignables, caminos } from "./arbol";
import { Cargando, ErrorDeCarga, Insignia, Pantalla, Progreso, Vacio } from "./componentes";
import { fecha, numero } from "./formato";
import { mensajeDeError } from "./mensajes";
import { fraseDeMeta, nombreDeMeta } from "./metas-texto";
import type { Navegar } from "./navegacion";

interface Borrador {
  territorio_id: number | null;
  objetivo: string;
  periodo_inicio: string;
  periodo_fin: string;
}

const VACIO: Borrador = { territorio_id: null, objetivo: "", periodo_inicio: "", periodo_fin: "" };

/**
 * METAS: la pregunta de B13 en una pantalla. Cada meta con su barra, su
 * veredicto («a este ritmo no llega») y lo que haría falta. Crear y ajustar
 * exigen `administrar_metas`; ver, `ver_metas`. El recurso es el del
 * manifiesto (db/005): esta pantalla no inventa rutas.
 */
export function Metas({
  yo,
  parametros,
  navegar,
  avisar,
}: {
  yo: Yo;
  parametros: Record<string, unknown>;
  navegar: Navegar;
  avisar: (t: string) => void;
}) {
  const [metas, setMetas] = useState<Meta[] | null>(null);
  const [nodos, setNodos] = useState<NodoTerritorio[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [borrador, setBorrador] = useState<Borrador | null>(parametros["nueva"] ? VACIO : null);
  const [editando, setEditando] = useState<number | null>(null);
  const puedeAdministrar = Boolean(yo.capacidades["administrar_metas"]);
  const puedeAsignar = Boolean(yo.capacidades["administrar_usuarios"]);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const t = await verTablero();
      setMetas(t.metas);
      // El árbol completo solo lo da /territorio a quien administra cuentas;
      // para quien no, el selector ofrece los territorios de sus metas.
      if (puedeAsignar) {
        const arbol = await listarTerritorio();
        setNodos(arbol.nodos);
      }
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }, [puedeAsignar]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const nombreDelNodo = useMemo(() => caminos(nodos), [nodos]);
  const asignables = useMemo(() => soloAsignables(nodos), [nodos]);
  const destacada = typeof parametros["meta"] === "number" ? (parametros["meta"] as number) : null;

  async function guardar() {
    if (!borrador) return;
    setError(null);
    const objetivo = Number(borrador.objetivo);
    if (!Number.isInteger(objetivo) || objetivo <= 0) {
      setError("El objetivo tiene que ser un número entero mayor que cero.");
      return;
    }
    try {
      const datos = {
        objetivo,
        periodo_inicio: borrador.periodo_inicio || null,
        periodo_fin: borrador.periodo_fin || null,
      };
      if (editando == null) {
        if (borrador.territorio_id == null) {
          setError("Elige el territorio de la meta.");
          return;
        }
        await crearMeta({ territorio_id: borrador.territorio_id, ...datos });
        avisar("Meta creada. El avance se cuenta solo desde ahora.");
      } else {
        await editarMeta(editando, datos);
        avisar("Meta ajustada.");
      }
      setBorrador(null);
      setEditando(null);
      await cargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  if (error && !metas) {
    return (
      <Pantalla titulo="Metas">
        <ErrorDeCarga mensaje={error} reintentar={() => void cargar()} />
      </Pantalla>
    );
  }
  if (!metas) {
    return (
      <Pantalla titulo="Metas">
        <Cargando que="las metas" />
      </Pantalla>
    );
  }

  return (
    <Pantalla
      titulo="Metas"
      descripcion="Cada meta dice si a este ritmo se llega y, si no, cuánto haría falta al día."
      acciones={
        puedeAdministrar && (
          <button
            type="button"
            className="btn primario"
            onClick={() => {
              setBorrador(VACIO);
              setEditando(null);
            }}
          >
            Nueva meta
          </button>
        )
      }
    >
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {borrador && (
        <div className="tarjeta formulario">
          <h2>{editando == null ? "Nueva meta" : "Ajustar la meta"}</h2>
          {editando == null && (
            <div className="campo">
              <label htmlFor="m-territorio">Territorio</label>
              {asignables.length > 0 ? (
                <select
                  id="m-territorio"
                  value={borrador.territorio_id ?? ""}
                  onChange={(e) =>
                    setBorrador({ ...borrador, territorio_id: e.target.value === "" ? null : Number(e.target.value) })
                  }
                >
                  <option value="">Elige un territorio</option>
                  {asignables.map((n) => (
                    <option key={n.id} value={n.id}>
                      {nombreDelNodo.get(n.id)}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  id="m-territorio"
                  value={borrador.territorio_id ?? ""}
                  onChange={(e) =>
                    setBorrador({ ...borrador, territorio_id: e.target.value === "" ? null : Number(e.target.value) })
                  }
                >
                  <option value="">Elige un territorio</option>
                  {yo.territorio && (
                    <option value={yo.territorio.id}>
                      {yo.territorio.nombre || `${yo.territorio.tipo} ${yo.territorio.clave}`}
                    </option>
                  )}
                  {[...new Map(metas.map((m) => [m.territorio_id, m])).values()]
                    .filter((m) => m.territorio_id !== yo.territorio?.id)
                    .map((m) => (
                      <option key={m.territorio_id} value={m.territorio_id}>
                        {nombreDeMeta(m)}
                      </option>
                    ))}
                </select>
              )}
              <p className="ayuda">La meta cuenta personas capturadas en las secciones de ese territorio.</p>
            </div>
          )}
          <div className="campo">
            <label htmlFor="m-objetivo">Objetivo (personas)</label>
            <input
              id="m-objetivo"
              inputMode="numeric"
              value={borrador.objetivo}
              onChange={(e) => setBorrador({ ...borrador, objetivo: e.target.value })}
            />
          </div>
          <div className="campos-en-fila">
            <div className="campo">
              <label htmlFor="m-inicio">Desde</label>
              <input
                id="m-inicio"
                type="date"
                value={borrador.periodo_inicio}
                onChange={(e) => setBorrador({ ...borrador, periodo_inicio: e.target.value })}
              />
            </div>
            <div className="campo">
              <label htmlFor="m-fin">Fecha límite</label>
              <input
                id="m-fin"
                type="date"
                value={borrador.periodo_fin}
                onChange={(e) => setBorrador({ ...borrador, periodo_fin: e.target.value })}
              />
              <p className="ayuda">Sin fecha límite el sistema estima cuándo llega, pero no puede decir si va tarde.</p>
            </div>
          </div>
          <div className="acciones">
            <button type="button" className="btn primario" onClick={() => void guardar()}>
              Guardar
            </button>
            <button
              type="button"
              className="btn terciario"
              onClick={() => {
                setBorrador(null);
                setEditando(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {metas.length === 0 ? (
        <Vacio
          titulo="Todavía no hay metas"
          detalle="Sin meta, el tablero solo puede decir cuántos llevas. Con meta dice si vas a llegar."
        />
      ) : (
        <ul className="metas-lista">
          {metas.map((m) => {
            const f = fraseDeMeta(m);
            return (
              <li key={m.id} className={`meta-resumen tarjeta${destacada === m.id ? " elegida" : ""}`}>
                <div className="meta-cabeza">
                  <div>
                    <span className="apagado chico">{m.tipo}</span>
                    <br />
                    <strong>{nombreDeMeta(m)}</strong>
                  </div>
                  <Insignia tono={f.tono}>{f.titulo}</Insignia>
                </div>
                <Progreso
                  valor={m.logrado}
                  max={m.objetivo}
                  tono={f.tono}
                  texto={`${numero(m.logrado)} de ${numero(m.objetivo)} · ${m.proyeccion.porcentaje} %`}
                />
                <p className="chico">{f.detalle}</p>
                <p className="apagado chico">
                  {m.periodo_inicio && m.periodo_fin
                    ? `Del ${fecha(m.periodo_inicio)} al ${fecha(m.periodo_fin)}`
                    : m.periodo_fin
                      ? `Hasta el ${fecha(m.periodo_fin)}`
                      : m.periodo_inicio
                        ? `Desde el ${fecha(m.periodo_inicio)}`
                        : "Sin periodo"}{" "}
                  · {numero(m.ultimos_14_dias)} en los últimos 14 días
                </p>
                <div className="acciones">
                  <button
                    type="button"
                    className="btn terciario"
                    onClick={() => navegar({ pantalla: "territorio", parametros: { nodo: m.territorio_id } })}
                  >
                    Ver el territorio
                  </button>
                  <button
                    type="button"
                    className="btn terciario"
                    onClick={() => navegar({ pantalla: "mapa", parametros: { nodo: m.territorio_id, capa: "cobertura" } })}
                  >
                    Ver en el mapa
                  </button>
                  {puedeAdministrar && (
                    <button
                      type="button"
                      className="btn terciario"
                      onClick={() => {
                        setEditando(m.id);
                        setBorrador({
                          territorio_id: m.territorio_id,
                          objetivo: String(m.objetivo),
                          periodo_inicio: m.periodo_inicio ?? "",
                          periodo_fin: m.periodo_fin ?? "",
                        });
                      }}
                    >
                      Ajustar
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Pantalla>
  );
}
